import { InlinePrompts, PromptTypes, StandalonePrompts } from "../constants/PromoTypesConstants";
import { HorizontalPositions, VerticalPositions } from "../constants/PositionConstants";
import PromoInteractionConstants, {
  getAllActivities,
} from "../constants/PromoInteractionConstants";
import { StatusesSimplifiedConstants } from "../constants/PromptStatusesConstants";
import getPromptStatus from "./getPromptStatus";
import { DefaultDeviceOptions } from "../constants/DevicesConstants";

export const hasInapp = prompt =>
  [
    DefaultDeviceOptions.roku_os.value,
    DefaultDeviceOptions.ios.value,
    DefaultDeviceOptions.android_os.value,
    DefaultDeviceOptions.android_tv.value,
    DefaultDeviceOptions.tv_os.value,
  ].includes(prompt.device_type);

export const supportsPopUpButtonFontSize = (prompt = {}) =>
  prompt.path_type === PromptTypes.retention_modal.value &&
  [
    DefaultDeviceOptions.android_os.value,
    DefaultDeviceOptions.android_tv.value,
    DefaultDeviceOptions.ios.value,
  ].includes(prompt.device_type);

/**
 * Whether a prompt exposes button layout controls (width, height, bottom padding).
 *
 * True only for native (Android/iOS) pop-up modals and when the controlling app flag is
 * enabled, matching the editor fields in RfRetentionButtons. Interstitials handle these
 * dimensions in their own preview component.
 *
 * @param {Object} [prompt={}] - Prompt with `path_type` and `device_type`.
 * @param {boolean} [flagEnabled=false] - The `native_popup_button_layout` app flag.
 * @returns {boolean} True when button layout controls apply.
 */
export const supportsPopUpButtonLayout = (prompt = {}, flagEnabled = false) =>
  flagEnabled &&
  prompt.path_type === PromptTypes.retention_modal.value &&
  [DefaultDeviceOptions.android_os.value, DefaultDeviceOptions.ios.value].includes(
    prompt.device_type,
  );
export const isPlacement = pathType => !!InlinePrompts?.[pathType]?.value;
export const isStandalone = (prompt = {}) => !!StandalonePrompts?.[prompt.path_type]?.value;
export const isModal = (prompt = {}) =>
  prompt.path_type !== StandalonePrompts.email.value &&
  StandalonePrompts?.[prompt.path_type]?.value;
export const isOverlay = (prompt = {}) =>
  [
    StandalonePrompts.retention_modal.value,
    StandalonePrompts.widget.value,
    StandalonePrompts.interstitial.value,
    StandalonePrompts.video.value,
    StandalonePrompts.bottom_banner.value,
  ].includes(prompt?.path_type);
export const isStandaloneWeb = (prompt = {}) =>
  prompt?.device_type === DefaultDeviceOptions.web.value &&
  [
    StandalonePrompts.retention_modal.value,
    StandalonePrompts.widget.value,
    StandalonePrompts.invisible.value,
    StandalonePrompts.interstitial.value,
    StandalonePrompts.video.value,
    StandalonePrompts.bottom_banner.value,
  ].includes(prompt?.path_type);

export const getButtonTitle = (prompt, key) => {
  if (!prompt?.filter?.is_translated) return prompt?.actions?.[key];
  return Object.values(prompt?.parsed_translations || {}).find(e => e?.default === "TRUE")?.[key];
};

export const displayInteractionType = (prompt, type) => {
  if (type === PromoInteractionConstants.accept.value)
    return (
      getButtonTitle(prompt, "rf_retention_button1_text") ||
      (prompt.path_type === "invisible" ? "accept" : "button 1")
    );
  if (type === PromoInteractionConstants.accept2.value)
    return getButtonTitle(prompt, "rf_retention_button2_text") || "button 2";
  if (type === PromoInteractionConstants.decline.value)
    return getButtonTitle(prompt, "rf_retention_button3_text") || "button 3";
  return type;
};
export const isEnhancedHTMLMode = (prompt = {}) =>
  [true, "true"].includes(prompt?.filter?.enhanced_html_mode);

/**
 * Whether Enhanced HTML Mode is offered for a prompt (drives the toggle's visibility).
 *
 * Scoped to web interstitial/retention_modal prompts on apps with the feature flag on.
 *
 * @param {Object} [prompt] - The prompt/path object.
 * @param {Object} [app] - The app object (read for `flags.allow_enhanced_html`).
 * @returns {boolean} True when the mode can be toggled for this prompt.
 */
export const supportsEnhancedHTML = (prompt = {}, app = {}) =>
  Boolean(app?.flags?.allow_enhanced_html) &&
  prompt?.device_type === DefaultDeviceOptions.web.value &&
  [StandalonePrompts.interstitial.value, StandalonePrompts.retention_modal.value].includes(
    prompt?.path_type,
  );

/**
 * Whether Enhanced HTML Mode is active — the mode is both supported and turned on.
 *
 * Guards behavior that must never trigger outside the supported context, even if a
 * stale `enhanced_html_mode` flag lingers on an unsupported prompt.
 *
 * @param {Object} [prompt] - The prompt/path object.
 * @param {Object} [app] - The app object (read for `flags.allow_enhanced_html`).
 * @returns {boolean} True when the code editor should replace the plain text fields.
 */
export const isEnhancedHTMLActive = (prompt = {}, app = {}) =>
  supportsEnhancedHTML(prompt, app) && isEnhancedHTMLMode(prompt);

export const isEmail = (prompt = { path_type: null }) =>
  prompt?.path_type === PromptTypes.email.value;

export const isInvisible = (prompt = { path_type: null }) =>
  prompt?.path_type === PromptTypes.invisible.value;

export const isText = (prompt = { path_type: null }) =>
  prompt?.path_type === PromptTypes.text.value;

export const isCustomGoalExists = (prompt = { custom_filter: {} }) =>
  !!Object.keys(prompt?.custom_filter || {}).length;

export const isAccept2Enabled = (prompt = { actions: {} }) =>
  [true, "true"].includes(prompt?.actions?.rf_settings_confirm_button_2_enabled) ||
  isEnhancedHTMLMode(prompt);
export const isDeclineEnabled = (prompt = { actions: {} }) =>
  [true, "true"].includes(prompt?.actions?.rf_settings_cancel_button_enabled) ||
  isEnhancedHTMLMode(prompt);
export const isTimeoutEnabled = (prompt = { actions: {} }) =>
  prompt?.actions?.rf_settings_close_seconds || isEnhancedHTMLMode(prompt);

export const isDismissEnabled = (prompt = { actions: {} }) => {
  return (
    [true, "true"].includes(prompt?.actions?.rf_settings_close_button_enabled) ||
    [true, "true"].includes(prompt?.actions?.rf_settings_click_outside_close_enabled) ||
    isEnhancedHTMLMode(prompt)
  );
};

export const hasRunningOrUnstartedExperiments = prompt => !!prompt?.experiment;
export const isSurvey = (prompt = { actions: {} }) =>
  [true, "true"].includes(prompt?.actions?.rf_retention_survey_selected);

// A prompt is a paywall when paywall tracking is enabled on it (RfPaywallConfig).
// Experiments have no effect on paywall prompts — every user receives the original variant (RE-1446).
export const isPaywall = (prompt = { actions: {} }) =>
  !!prompt?.actions?.rf_retention_paywall_tracker_id;

export const getPosition = (actions, mobile = false) => {
  const key = mobile ? "rf_mobile_widget_position" : "rf_widget_position";
  const pos = { y: VerticalPositions.bottom.value, x: HorizontalPositions.right.value };
  if (!actions?.[key]) return pos;

  const [verticalPos, horizontalPos] = actions[key].split("_");

  if (VerticalPositions[verticalPos]) pos.y = VerticalPositions[verticalPos].value;
  if (HorizontalPositions[horizontalPos]) pos.x = HorizontalPositions[horizontalPos].value;
  return pos;
};

export const getPositionStyle = (
  actions = {},
  options = {
    offsetYKey: "rf_banner_position_offset_y",
    offsetXKey: "rf_banner_position_offset_x",
  },
) => {
  const promptStyles = {
    "top": "inherit",
    "bottom": "inherit",
    "left": "inherit",
    "right": "inherit",
    "animation-name": "animatetop",
  };
  const position = getPosition(actions);
  promptStyles[position.y] = actions[options.offsetYKey] || "0px";
  promptStyles["animation-name"] = `animate${position.y}`;

  if (position.x === HorizontalPositions.center.value) return promptStyles;

  promptStyles[position.x] = actions[options.offsetXKey] || "0px";
  return promptStyles;
};

export const isLatestExperimentActive = ({ path, experiments }) => {
  if (path?.experiment) return !!path.experiment.is_active && !path.experiment.is_completed;
  if (Array.isArray(experiments)) return !!experiments.at(-1)?.is_active;
  return false;
};

export const isLatestExperimentUnstarted = ({ path, experiments }) => {
  if (path?.experiment) return !path.experiment.is_active && !path.experiment.is_completed;
  if (Array.isArray(experiments))
    return !experiments.at(-1)?.is_active && !experiments.at(-1)?.is_completed;
  return false;
};

export const isLocalized = prompt => prompt?.filter?.is_translated;
export const isPushNotification = prompt => prompt?.path_type === PromptTypes.scheduled_push.value;
export const isScheduledTriggeredPush = prompt =>
  isPushNotification(prompt) && prompt?.actions?.rf_pinpoint_campaign_event_triggered !== "true";

export const getAllActivitiesForPrompt = prompt => {
  const activities = getAllActivities();
  if (isInvisible(prompt)) {
    return ["goal", "custom_goals_seen", "custom_goals_holdout", "holdout"].reduce(
      (hash, key) => ({ ...hash, [key]: activities[key] }),
      {},
    );
  }
  if (isText(prompt)) {
    delete activities.goal2;
    delete activities.decline;
    return activities;
  }
  ["goal", "goal2", "decline"].forEach((key, i) => {
    const promptButtonText = prompt.actions?.[`rf_retention_button${i + 1}_text`];
    if (promptButtonText) {
      activities[key].value += `: ${promptButtonText}`;
    }
  });

  if (!isAccept2Enabled(prompt)) {
    delete activities.goal2;
  }

  if (!isDismissEnabled(prompt)) {
    delete activities.dismiss;
  }

  if (!isTimeoutEnabled(prompt)) {
    delete activities.timeout;
  }

  if (!isDeclineEnabled(prompt)) {
    delete activities.decline;
  }

  if (!isCustomGoalExists(prompt)) {
    delete activities.custom_goals_seen;
    delete activities.custom_goals_holdout;
  }

  if (!hasRunningOrUnstartedExperiments(prompt)) {
    delete activities.holdout;
  }

  return activities;
};

export const statusActionsForPrompt = (prompt, timezone) => {
  const { running, ready, limited } = StatusesSimplifiedConstants;
  return Object.values(StatusesSimplifiedConstants).filter(status => {
    if (status.type === ready.type) {
      return !prompt.is_enabled;
    }
    if (status.type === running.type) {
      return !isScheduledTriggeredPush(prompt);
    }
    return status.type !== limited.type && status.type !== getPromptStatus(prompt, timezone).type;
  });
};

export const disabledActionsForPrompt = prompt => {
  if (!prompt || !isPushNotification(prompt)) return [];

  const { rf_retention_title, rf_retention_message, rf_push_action } = prompt.actions;

  if (!prompt.segments?.length || !rf_retention_title || !rf_retention_message || !rf_push_action) {
    return ["running", "ready"];
  }

  return [];
};
